import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-y-2 px-2 lg:px-8 lg:gap-y-8"
}
const _hoisted_2 = { class: "grid grid-cols-1 grid-rows-auto lg:grid-cols-3 gap-2 lg:gap-4" }
const _hoisted_3 = { class: "flex flex-col gap-2" }
const _hoisted_4 = { class: "flex flex-col gap-2" }
const _hoisted_5 = { class: "flex flex-col gap-2" }
const _hoisted_6 = { class: "flex justify-center" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("i", { class: "pi pi-download" }, null, -1)

import CButton from "@/components/Button.vue";
import VCalendar from "./components/Calendar.vue";
import CSwitch from "@/components/shared/Switch.vue";
import Filter from "@/components/shared/Filter.vue";
import { queryParamTOArray } from "@/assets/utils/index";
import { dateToISO, getMonthStartAndEnd } from "@/assets/utils/date";
import { compareAsc, isValid } from "date-fns";
import { cloneDeep, debounce } from "lodash";
import { useRoute, useRouter } from "vue-router";
import { onBeforeMount, ref, watch } from "vue";
import store from "@/store";
import { computed } from "@vue/reactivity";
import { reservationStatusList } from "@/assets/utils/translatedEnums";
import { useI18n } from "vue-i18n";
import {
  filterByServices,
  filterBySpaces,
  filterByStatus,
} from "@/assets/utils/reservations";
import { StartOfMonth } from "@/assets/utils/StartOfMonth";
import { EndOfMonth } from "@/assets/utils/EndOfMonth";

export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const userInfo = computed(() => store.getters["user/info"]);
const download = async () => {
  await store.dispatch("reservation/DOWNLOAD", {
    startDate: settings.value.startDate,
    endDate: settings.value.endDate,
  });
};
const monthChange = ({ month, year }: { month: number; year: number }) => {
  const { startOfMonth, endOfMonth } = getMonthStartAndEnd(month, year);
  settings.value.startDate = startOfMonth;
  settings.value.endDate = endOfMonth;
};
const debounceMonthChange = debounce(monthChange, 500);

const spaces = computed(() => store.getters["pos/spaces"]);
const services = computed(() => store.getters["pos/services"]);

const init = () => {
  const q = route.query;
  const startDate = isValid(new Date(q.startDate as string))
    ? (q.startDate as string)
    : dateToISO({
        date: StartOfMonth(new Date()),
        withHHMM: false,
      });
  let endDate = dateToISO({
    date: EndOfMonth(new Date()),
    withHHMM: false,
  });
  if (isValid(new Date(q.endDate as string))) {
    endDate =
      compareAsc(new Date(q.endDate as string), new Date(startDate)) >= 0
        ? dateToISO({ date: new Date(q.endDate as string), withHHMM: false })
        : endDate;
  }
  return cloneDeep({
    spacesIds: queryParamTOArray(q.spaceId as any),
    isOutOfService: (q.isOutOfService as any) === "false" ? false : true,
    isWithoutSpace: (q.isWithoutSpace as any) === "false" ? false : true,
    isUnknownStatus: (q.isUnknownStatus as any) === "false" ? false : true,
    servicesIds: queryParamTOArray(q.serviceId as any),
    statusIds: queryParamTOArray(q.statusId as any),
    startDate,
    endDate,
  });
};
let settings = ref<any>();
onBeforeMount(async () => {
  settings.value = init();
  // await fetchReservations();
});
const rawReservations = ref<any>();
const reservations = computed(() => {
  if (!rawReservations.value) {
    return null;
  }

  let result = cloneDeep(rawReservations.value);
  // console.log("result before any filter", result);
  const filtredServices: any =
    (settings.value.servicesIds ?? []).length > 0
      ? cloneDeep(
          settings.value.servicesIds?.map((id: string) =>
            services.value.find((s: any) => s.id === id)
          )
        )
      : cloneDeep(store.getters["pos/services"]);

  result = filterByStatus({
    reservations: cloneDeep(result),
    statuses:
      (settings.value.statusIds ?? []).length > 0
        ? cloneDeep(settings.value.statusIds ?? [])
        : cloneDeep((reservationStatusList(t) ?? []).map((s: any) => s.id)),
    includeWithoutStatus: settings.value.isUnknownStatus,
  });
  // console.log("result after status", result);

  result = filterBySpaces({
    reservations: cloneDeep(result),
    spaces:
      (settings.value.spacesIds ?? []).length > 0
        ? cloneDeep(settings.value.spacesIds ?? [])
        : cloneDeep((spaces.value ?? []).map((s: any) => s.id)),
    includeWithoutSpace: settings.value.isWithoutSpace,
  });
  // console.log("result after spaces", result);
  result = filterByServices({
    reservations: cloneDeep(result),
    services: filtredServices,
    includeOutOfService: settings.value.isOutOfService,
  });
  // console.log("result after services", result);
  return result;
});
const fetchReservations = async () => {
  rawReservations.value = await store.dispatch("reservation/RETRIEVE", {
    startDate: settings.value.startDate,
    endDate: settings.value.endDate,
  });
};
const debounceFetchReservations = debounce(fetchReservations, 500);
watch(
  () => route,
  async (previous, current) => {
    if (previous.name === current.name) {
      settings.value = init();
    }
  },
  {
    deep: true,
  }
);
watch(
  settings,
  async (change) => {
    const query: any = {};
    query.startDate = change.startDate;
    query.endDate = change.endDate;
    query.statusId = change.statusIds;
    query.spaceId = change.spacesIds;
    query.serviceId = change.servicesIds;
    query.isOutOfService = change.isOutOfService;
    query.isWithoutSpace = change.isWithoutSpace;
    query.isUnknownStatus = change.isUnknownStatus;

    await debounceFetchReservations();
    await router.replace({ name: "Home", query });
  },
  { deep: true }
);

return (_ctx: any,_cache: any) => {
  return (_unref(userInfo))
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createVNode(Filter, {
              "all-word": _ctx.$t('allSpaces'),
              options: _unref(spaces),
              modelValue: _unref(settings).spacesIds,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((_unref(settings).spacesIds) = $event)),
              placeholder: _ctx.$t('space')
            }, null, 8, ["all-word", "options", "modelValue", "placeholder"]),
            _createVNode(CSwitch, {
              modelValue: _unref(settings).isWithoutSpace,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((_unref(settings).isWithoutSpace) = $event))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("include")) + " " + _toDisplayString(_ctx.$t("WithoutASpace")), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_4, [
            _createVNode(Filter, {
              "all-word": _ctx.$t('allServices'),
              options: _unref(services),
              modelValue: _unref(settings).servicesIds,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(settings).servicesIds) = $event)),
              placeholder: _ctx.$t('service')
            }, null, 8, ["all-word", "options", "modelValue", "placeholder"]),
            _createVNode(CSwitch, {
              modelValue: _unref(settings).isOutOfService,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((_unref(settings).isOutOfService) = $event))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("include")) + " " + _toDisplayString(_ctx.$t("OUT_OF_SLOT")), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _createElementVNode("div", _hoisted_5, [
            _createVNode(Filter, {
              "all-word": _ctx.$t('allStatuses'),
              options: _unref(reservationStatusList)(_ctx.$t),
              modelValue: _unref(settings).statusIds,
              "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((_unref(settings).statusIds) = $event)),
              placeholder: _ctx.$t('status')
            }, null, 8, ["all-word", "options", "modelValue", "placeholder"]),
            _createVNode(CSwitch, {
              modelValue: _unref(settings).isUnknownStatus,
              "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((_unref(settings).isUnknownStatus) = $event))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t("include")) + " " + _toDisplayString(_ctx.$t("unknownStatus")), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ])
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createVNode(CButton, {
            onClick: _cache[6] || (_cache[6] = ($event: any) => (download()))
          }, {
            default: _withCtx(() => [
              _hoisted_7,
              _createTextVNode(" " + _toDisplayString(_ctx.$t("download")), 1)
            ]),
            _: 1
          })
        ]),
        (_unref(reservations))
          ? (_openBlock(), _createBlock(VCalendar, {
              key: 0,
              onMonthChange: _unref(debounceMonthChange),
              url: "/reservation/list",
              reservations: _unref(reservations),
              class: "w-full"
            }, null, 8, ["onMonthChange", "reservations"]))
          : _createCommentVNode("", true)
      ]))
    : _createCommentVNode("", true)
}
}

})